/**
 * useWaLazyMedia — Resuelve on-demand la URL de media WhatsApp.
 *
 * Patrón actual: el webhook descarga el media SINCRÓNICO antes del
 * INSERT, así los mensajes nuevos llegan al frontend con media_url ya
 * poblada y renderizan al toque (estilo WhatsApp Web).
 *
 * Este hook se usa solo para HISTORIAL ANTIGUO — mensajes pre-fix con
 * media_status='pending' o NULL y media_url=null (típicamente importados
 * por wa-history-sync, o creados antes del refactor sincrónico). Lazy
 * load con IntersectionObserver: dispara wa-fetch-media solo cuando el
 * mensaje entra al viewport, ahorrando red.
 *
 * Cache de módulo evita re-fetch entre renders/scrolling.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

const resolvedCache = new Map<string, string>();
const MEDIA_TYPES = new Set(['image', 'audio', 'voice', 'video', 'document', 'sticker']);

// Cooldown anti-loop: no re-disparar fetch del mismo messageId en <60s
// cuando el server reportó 'failed'. Después de 60s permitimos un nuevo
// intento en caso que Evolution se haya recuperado o la red mejoró.
const failedRetryAt = new Map<string, number>();
const FAILED_RETRY_COOLDOWN_MS = 60_000;

interface Options {
  messageId: string;
  mediaUrl: string | null;
  mediaStatus: string | null;
  msgType: string;
  /** Pasthrough — el flujo nuevo no lo usa, pero lo dejamos por si en
   *  algún caso edge necesitamos reintroducir lógica por edad. */
  createdAt?: string | null;
}

interface Result {
  resolvedUrl: string | null;
  loading: boolean;
  failed: boolean;
  /** Attach este ref al contenedor del mensaje para activar IntersectionObserver */
  observeRef: React.RefObject<HTMLDivElement>;
}

export function useWaLazyMedia({ messageId, mediaUrl, mediaStatus, msgType }: Options): Result {
  const observeRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);

  // URLs blob: optimistic UI temporales del agente (file recién adjunto).
  // NO cachear: cuando llega la URL pública del Storage la prop cambia, y
  // el revoke de la blob deja un src roto si quedó cacheada.
  const isPersistentUrl = (u: string | null | undefined): boolean =>
    !!u && !u.startsWith('blob:');

  const [state, setState] = useState<{ url: string | null; loading: boolean; failed: boolean }>(() => {
    const cached = resolvedCache.get(messageId);
    if (cached) return { url: cached, loading: false, failed: false };
    if (mediaUrl) return { url: mediaUrl, loading: false, failed: false };
    // 'expired' es permanente (Evolution no tiene la media), 'failed' es
    // recuperable (auto-retry on viewport con cooldown). Para 'failed' no
    // marcamos el state como failed inicialmente: dejamos que la lazy
    // fetch lo intente. Si falla, se marca failed (local).
    const alreadyFailed = mediaStatus === 'expired';
    return { url: null, loading: false, failed: alreadyFailed };
  });

  // Sincroniza state.url cuando la prop media_url cambia (optimistic
  // blob → URL pública post-upload, o realtime UPDATE en mensajes live).
  useEffect(() => {
    if (!mediaUrl) return;
    const cached = resolvedCache.get(messageId);
    if (cached === mediaUrl) return;
    if (isPersistentUrl(mediaUrl)) {
      resolvedCache.set(messageId, mediaUrl);
    }
    setState({ url: mediaUrl, loading: false, failed: false });
  }, [mediaUrl, messageId]);

  const doFetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setState((s) => ({ ...s, loading: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('no session');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-fetch-media`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ message_id: messageId }),
        },
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = (await res.json()) as { url?: string };
      if (!url) throw new Error('empty url');

      resolvedCache.set(messageId, url);
      setState({ url, loading: false, failed: false });
    } catch {
      fetchedRef.current = false; // allow manual retry
      // Marcar timestamp para cooldown — evita re-fetch en próximos 60s.
      failedRetryAt.set(messageId, Date.now());
      setState({ url: null, loading: false, failed: true });
    }
  }, [messageId]);

  useEffect(() => {
    // Solo actuamos en mensajes sin URL persistente.
    if (state.url) return;
    if (!MEDIA_TYPES.has(msgType)) return;
    // Auto-retry para 'pending', NULL Y 'failed' (con cooldown).
    // 'expired' es permanente — no reintentamos.
    if (mediaStatus === 'expired') return;

    // Cooldown: si tuvimos un fetch fallido del mismo msg en los últimos 60s,
    // no re-intentar (evita loops cuando el usuario hace scroll arriba/abajo
    // en mensajes que Evolution no puede recuperar).
    if (mediaStatus === 'failed') {
      const lastTry = failedRetryAt.get(messageId);
      if (lastTry && Date.now() - lastTry < FAILED_RETRY_COOLDOWN_MS) {
        return;
      }
    }

    // Lazy load con IntersectionObserver: solo descarga cuando el msg
    // entra (o se acerca a) viewport. Apropiado para historial.
    const el = observeRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          doFetch();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [state.url, mediaStatus, msgType, doFetch]);

  return { resolvedUrl: state.url, loading: state.loading, failed: state.failed, observeRef };
}
